<template>
  <div class="search-page">
    <!-- Search Bar -->
    <div class="search-section">
      <div class="search-bar-wrapper">
        <el-input
          v-model="searchQuery"
          :placeholder="$t('nav.searchPlaceholder')"
          class="search-input"
          @keyup.enter.native="handleSearch"
        >
          <template slot="suffix">
            <span class="search-icon-btn" @click="handleSearch">
              <img src="@/assets/images/search.png" alt="search" class="search-icon" />
            </span>
          </template>
        </el-input>
      </div>
    </div>

    <!-- Results -->
    <div class="search-inner">
      <h2 class="result-title">
        {{ $t("search.resultPrefix") }}「<span class="highlight">{{ activeQuery }}</span>」（{{ $t("search.resultCountPrefix") }}<span class="highlight">{{ total }}</span>{{ $t("search.resultCountSuffix") }}）
      </h2>

      <!-- Sort -->
      <div class="sort-bar">
        <span class="sort-label">{{ $t("explore.sortBy") }}</span>
        <span class="sort-option" @click="onSortToggle">
          {{ $t("explore.sortPublishDate") }}
          <i :class="orderType === 1 ? 'el-icon-top' : 'el-icon-bottom'"></i>
        </span>
      </div>

      <!-- Book Grid / Empty -->
      <div class="book-results" v-loading="loading">
        <div v-if="bookList.length" class="book-grid">
          <div
            v-for="book in bookList"
            :key="book.id"
            class="book-grid-item"
          >
            <book-card :book="book" @click="goToBookDetail(book)" />
          </div>
        </div>
        <el-empty
          v-else-if="!loading"
          :description="$t('common.empty')"
        ></el-empty>
      </div>

      <!-- Pagination -->
      <div v-if="total > pageSize" class="pagination-wrapper">
        <el-pagination
          background
          layout="prev, pager, next"
          :total="total"
          :page-size="pageSize"
          :current-page.sync="currentPage"
          @current-change="handlePageChange"
        ></el-pagination>
      </div>
    </div>
  </div>
</template>

<script>
import BookCard from "@/components/BookCard.vue";
import { getImage, formatAuthorName } from "@/utils/tools";
import { listBookPage } from "@/api/book";

export default {
  name: "SearchPage",
  components: { BookCard },
  data() {
    return {
      searchQuery: "",
      activeQuery: "",
      orderType: 2,
      currentPage: 1,
      pageSize: 10,
      total: 0,
      bookList: [],
      loading: false,
    };
  },
  watch: {
    "$route.query.q": {
      immediate: true,
      handler(val) {
        this.searchQuery = val || "";
        this.activeQuery = val || "";
        this.currentPage = 1;
        this.loadBookList();
      },
    },
    '$i18n.locale'() {
      this.bookList.forEach((book) => {
        book.author = formatAuthorName(book.authorList, book.authorName);
      });
    },
  },
  methods: {
    async loadBookList() {
      this.loading = true;
      try {
        const res = await listBookPage({
          pageNum: this.currentPage,
          pageSize: this.pageSize,
          orderField: 1,
          orderType: this.orderType,
          searchValue: this.activeQuery || "",
        });
        const rows = Array.isArray(res?.rows) ? res.rows : [];
        this.bookList = rows.map((item) => ({
          id: item.bookId,
          title: item.bookName,
          titleEn: item.bookNameEn,
          authorList: item.authorList,
          authorName: item.authorName,
          author: formatAuthorName(item.authorList, item.authorName),
          date: item.publicationDate,
          cover: item.bookImageAddr ? getImage(item.bookImageAddr) : null,
        }));
        this.total = Number(res?.total) || 0;
      } catch (err) {
        this.bookList = [];
        this.total = 0;
      } finally {
        this.loading = false;
      }
    },
    handleSearch() {
      const q = this.searchQuery.trim();
      if (!q) return;
      if (q === this.$route.query.q) {
        this.activeQuery = q;
        this.currentPage = 1;
        this.loadBookList();
      } else {
        this.$router.replace({ query: { q } }).catch(() => {});
      }
    },
    onSortToggle() {
      this.orderType = this.orderType === 2 ? 1 : 2;
      this.currentPage = 1;
      this.loadBookList();
    },
    handlePageChange() {
      this.loadBookList();
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    goToBookDetail(book) {
      const route = this.$router.resolve({ path: `/book/${book.id}` });
      window.open(route.href, "_blank");
    },
  },
};
</script>

<style lang="scss" scoped>
.search-section {
  background: linear-gradient(270deg, #fe7819 0%, #ffa826 100%);
  padding: 24px;
}

.search-bar-wrapper {
  max-width: 600px;
  margin: 0 auto;

  .search-input {
    width: 100%;

    ::v-deep .el-input__inner {
      border-radius: 20px;
      border: none;
      height: 40px;
      line-height: 40px;
      font-size: 14px;
      padding-left: 20px;
      padding-right: 50px;
      box-shadow: none;
    }

    ::v-deep .el-input__suffix {
      right: 5px;
      display: flex;
      align-items: center;
    }
  }

  .search-icon-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    cursor: pointer;

    &:hover {
      opacity: 0.7;
    }
  }

  .search-icon {
    width: 20px;
    height: 20px;
  }
}

.search-inner {
  max-width: $max-width;
  margin: 0 auto;
  padding: 30px 24px 40px;
}

.result-title {
  font-size: 20px;
  color: $text-dark;
  margin-bottom: 20px;
  font-weight: 700;

  .highlight {
    color: $primary-orange;
  }
}

.sort-bar {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  margin-bottom: 24px;
  font-size: 16px;
  color: $text-gray;

  .sort-label {
    font-size: 16px;
  }

  .sort-option {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    font-size: 16px;
    color: $primary-orange;
    cursor: pointer;
    user-select: none;

    i {
      font-weight: 700;
      font-size: 16px;
    }

    &:hover {
      opacity: 0.8;
    }
  }
}

.book-results {
  min-height: 200px;
}

.book-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 38px;
}

.book-grid-item {
  width: 200px;
  height: 430px;
  // padding: 16px 20px;
  box-sizing: border-box;
  border-radius: 5px;
  // border: 1px solid #DADADA;
  transition: transform 0.2s, box-shadow 0.2s;
  cursor: pointer;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  }

  ::v-deep .book-card {
    &:hover {
      transform: none;
    }
  }
}

.pagination-wrapper {
  display: flex;
  justify-content: center;
  margin-top: 20px;
}
</style>
